import { schema } from 'normalizr';
import { RSAA, createAction } from 'redux-api-middleware';
import { handleResponse, handleError, buildQueryString } from '../utils';

export const FETCH_ADVANCEGROUPS_REQUEST = 'FETCH_ADVANCEGROUPS_REQUEST';
export const FETCH_ADVANCEGROUPS_SUCCESS = 'FETCH_ADVANCEGROUPS_SUCCESS';
export const FETCH_ADVANCEGROUPS_FAILURE = 'FETCH_ADVANCEGROUPS_FAILURE';

export const FETCH_ADVANCEGROUPDETAILS_REQUEST = 'FETCH_ADVANCEGROUPDETAILS_REQUEST';
export const FETCH_ADVANCEGROUPDETAILS_SUCCESS = 'FETCH_ADVANCEGROUPDETAILS_SUCCESS';
export const FETCH_ADVANCEGROUPDETAILS_FAILURE = 'FETCH_ADVANCEGROUPDETAILS_FAILURE';

export const CREATE_ADVANCEGROUP_REQUEST = 'CREATE_ADVANCEGROUP_REQUEST';
export const CREATE_ADVANCEGROUP_SUCCESS = 'CREATE_ADVANCEGROUP_SUCCESS';
export const CREATE_ADVANCEGROUP_FAILURE = 'CREATE_ADVANCEGROUP_FAILURE';

export const EDIT_ADVANCEGROUP_REQUEST = 'EDIT_ADVANCEGROUP_REQUEST';
export const EDIT_ADVANCEGROUP_SUCCESS = 'EDIT_ADVANCEGROUP_SUCCESS';
export const EDIT_ADVANCEGROUP_FAILURE = 'EDIT_ADVANCEGROUP_FAILURE';

export const FETCH_ADVANCELOGS_REQUEST = 'FETCH_ADVANCELOGS_REQUEST';
export const FETCH_ADVANCELOGS_SUCCESS = 'FETCH_ADVANCELOGS_SUCCESS';
export const FETCH_ADVANCELOGS_FAILURE = 'FETCH_ADVANCELOGS_FAILURE';

export const FETCH_ADVANCEGROUPACTIVITY_REQUEST = 'FETCH_ADVANCEGROUPACTIVITY_REQUEST';
export const FETCH_ADVANCEGROUPACTIVITY_SUCCESS = 'FETCH_ADVANCEGROUPACTIVITY_SUCCESS';
export const FETCH_ADVANCEGROUPACTIVITY_FAILURE = 'FETCH_ADVANCEGROUPACTIVITY_FAILURE';

export const DELETE_ADVANCEGROUP_REQUEST = 'DELETE_ADVANCEGROUP_REQUEST';
export const DELETE_ADVANCEGROUP_SUCCESS = 'DELETE_ADVANCEGROUP_SUCCESS';
export const DELETE_ADVANCEGROUP_FAILURE = 'DELETE_ADVANCEGROUP_FAILURE';

export const ADVANCEGROUPS_PAGE_SIZE = 20;

export const advanceGroupSchema = new schema.Entity(
  'advancegroups',
  {},
  {
    idAttribute: '_id',
    processStrategy(advanceGroup) {
      return {
        ...advanceGroup,
      };
    },
  },
);

/**
 * Fetch advancegroups.
 *
 * @return {object} - Redux api middleware action that triggers & handles the request.
 */
export const fetchAdvanceGroups = ({
  start = 0,
  count = ADVANCEGROUPS_PAGE_SIZE,
  query,
  status,
  contractId,
}) =>
  createAction({
    endpoint: `${window.config.apiUrl}/search/advancegroups?${buildQueryString({
      start,
      count,
      q: query,
      status,
      contractId,
    })}`,
    method: 'GET',
    types: [
      { type: FETCH_ADVANCEGROUPS_REQUEST },
      {
        type: FETCH_ADVANCEGROUPS_SUCCESS,
        payload: (action, state, res) => handleResponse([advanceGroupSchema], action, state, res),
        meta: (action, state, res) => ({
          totalCount: parseInt(res.headers.get('Total-Count'), 10),
        }),
      },
      { type: FETCH_ADVANCEGROUPS_FAILURE, payload: handleError },
    ],
  });

/**
 * Create a new advanceGroup
 * @param {object} values - Object containing advanceGroup form values
 * @return {object} - Redux api middleware action that triggers & handles the request.
 */
export const createAdvanceGroup = values =>
  createAction({
    endpoint: `${window.config.apiUrl}/advancegroups/save`,
    method: 'POST',
    body: JSON.stringify(values),
    types: [
      { type: CREATE_ADVANCEGROUP_REQUEST },
      {
        type: CREATE_ADVANCEGROUP_SUCCESS,
        payload: (action, state, res) => handleResponse(advanceGroupSchema, action, state, res),
      },
      {
        type: CREATE_ADVANCEGROUP_FAILURE,
        payload: handleError,
      },
    ],
  });

/**
 * Save an existing advanceGroup
 * @param {object} values - Object containing advanceGroup form values
 * @return {object} - Redux api middleware action that triggers & handles the request.
 */
export const editAdvanceGroup = (values, id) =>
  createAction({
    endpoint: `${window.config.apiUrl}/advancegroups/${id}/save`,
    method: 'PATCH',
    body: JSON.stringify(values),
    types: [
      { type: EDIT_ADVANCEGROUP_REQUEST },
      {
        type: EDIT_ADVANCEGROUP_SUCCESS,
        payload: (action, state, res) => handleResponse(advanceGroupSchema, action, state, res),
      },
      {
        type: EDIT_ADVANCEGROUP_FAILURE,
        payload: handleError,
      },
    ],
  });

/**
 * Get the details of an existing advanceGroup
 * @param {String} advancegroupId - _id of the requested advance group
 * @returns {object} - Redux api middleware action that triggers & handles the request.
 */
export const fetchAdvanceGroupDetails = advancegroupId => ({
  [RSAA]: {
    endpoint: `${window.config.apiUrl}/advancegroups/${advancegroupId}`,
    method: 'GET',
    types: [
      { type: FETCH_ADVANCEGROUPDETAILS_REQUEST },
      {
        type: FETCH_ADVANCEGROUPDETAILS_SUCCESS,
        payload: (action, state, res) => handleResponse(advanceGroupSchema, action, state, res),
      },
      { type: FETCH_ADVANCEGROUPDETAILS_FAILURE, payload: handleError },
    ],
  },
});

export const advanceLogsSchema = new schema.Entity(
  'advancelogs',
  {},
  {
    idAttribute: '_id',
    processStrategy(advanceLog) {
      return {
        ...advanceLog,
      };
    },
  },
);

/**
 * Get the advance logs of an existing advanceGroup
 * @param {String} advancegroupId - id of the requested advance group
 * @returns {object} - Redux api middleware action that triggers & handles the request
 */
export const fetchAdvanceLogs = advancegroupId => ({
  [RSAA]: {
    endpoint: `${window.config.apiUrl}/advancegroups/${advancegroupId}/advancelogs`,
    method: 'GET',
    types: [
      { type: FETCH_ADVANCELOGS_REQUEST },
      {
        type: FETCH_ADVANCELOGS_SUCCESS,
        payload: (action, state, res) => handleResponse(advanceLogsSchema, action, state, res),
        meta: { advancegroupId },
      },
      {
        type: FETCH_ADVANCELOGS_FAILURE,
        payload: handleError,
      },
    ],
  },
});

export const activitySchema = new schema.Entity('activity', {}, { idAttribute: '_id' });

export const fetchAdvanceGroupActivity = advancegroupId => ({
  [RSAA]: {
    endpoint: `${window.config.apiUrl}/advancegroups/${advancegroupId}/activity`,
    method: 'GET',
    types: [
      { type: FETCH_ADVANCEGROUPACTIVITY_REQUEST },
      {
        type: FETCH_ADVANCEGROUPACTIVITY_SUCCESS,
        payload: (action, state, res) => handleResponse([activitySchema], action, state, res),
        meta: { adjustmentId: advancegroupId },
      },
      {
        type: FETCH_ADVANCEGROUPACTIVITY_FAILURE,
        payload: handleError,
      },
    ],
  },
});

export const deleteAdvanceGroup = advancegroupId => {
  return createAction({
    endpoint: `${window.config.apiUrl}/advancegroups/${advancegroupId}`,
    method: 'DELETE',
    types: [
      { type: DELETE_ADVANCEGROUP_REQUEST },
      {
        type: DELETE_ADVANCEGROUP_SUCCESS,
        meta: (action, state, res) => ({ resMessage: res.message }),
      },
      { type: DELETE_ADVANCEGROUP_FAILURE, payload: handleError },
    ],
  });
};
